import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { withFormik, FieldArray } from 'formik';
import styled, { withTheme } from 'styled-components';
import { range } from 'lodash';
import { withRouter } from 'next/router';
import { TrackClick } from '@g4/trackster';
import tracking from '@g4/g4-analytics';

import CustomSelectNoSSR from '../DataEntry/CustomSelectNoSSR';

import Layout from '@g4/boneyard/lib/Layout';
import Box from '@g4/boneyard/lib/Box';
import { Media } from '@g4/boneyard/lib/Media';
import { Button } from '../Boneyard';
import VerticalSpacing from '@g4/boneyard/lib/VerticalSpacing';
import moment from '@g4/moment-utc';
import {
  withValue,
  OPEN_CHECK_IN,
  OPEN_CHECKOUT,
  DECREMENT_ROOMS,
  INCREMENT_ROOMS,
  ROOMS_QUANTITY,
  DECREMENT_ADULTS,
  ADULTS_QUANTITY,
  INCREMENT_ADULTS,
  DECREMENT_CHILDREN,
  INCREMENT_CHILDREN,
  CHILDREN_QUANTITY,
  SUBMIT_HOTEL_SEARCH,
  REMOVE_ROOM,
  CLOSE_SEARCH_BUTTON,
  SELECT_CHILD_AGE,
} from '@g4/automation-hooks';

import FieldHandler from '../FieldHandler';
import PlainButton from '../PlainButton';
import TempQuantitySelector from '../TempQuantitySelector';
import { MODE_START, MODE_END } from '../DateRangePicker';

import Text from '../Boneyard/Text';
import DatePicker from './DatePicker';
import InputMask from './InputMask';
import FieldName from './FieldName';
import OutsideListener from '../OutsideListener';
import Markdown from '../Markdown';
import { AnalyticsContext } from '../SiteLayout/SiteLayout';
import {
  DESTINATION_FIELD,
  CHECK_IN_FIELD,
  CHECK_OUT_FIELD,
  ROOMS_FIELD,
  ROOM_PARTY,
  CHILDREN_AGES,
  ADULTS_FIELD,
  CHILDREN_FIELD,
} from '../../utils/fieldsNames';
import { GA4_EVENTS } from '../../utils/constants/trackingConstants';

const { Row, Col } = Layout;

const searchFormSchema = Yup.object().shape({
  [DESTINATION_FIELD]: Yup.string().required(),
  [CHECK_IN_FIELD]: Yup.string().required(),
  [CHECK_OUT_FIELD]: Yup.string().required(),
  [ROOM_PARTY]: Yup.array().of(
    Yup.object().shape({
      [CHILDREN_AGES]: Yup.array().of(Yup.number().min(0)),
    })
  ),
});

const SearchFormButton = styled(Button)`
  width: ${props => (props.editMode ? '200px' : '300px')};
  order: 1;
  ${props => props.theme.query.md} {
    order: 1;
  }
  ${props => props.theme.query.xs} {
    width: 100%;
  }
`;

const Divider = styled.div`
  width: '100%';
  border-top: ${props => props.theme.colors.hob.destinationPicker.divider} 1px
    solid;
`;

const Label = styled.label`
  display: block;
`;

const FloatedCol = styled(Col)`
  position: absolute;
  bottom: 0;
`;

const getRowGutter = mq => (mq.lte('lg') ? 15 : 20);

const formatList = destinations =>
  destinations.map(destination => ({
    value: destination.id,
    label: destination.title,
  }));

class SearchForm extends Component {
  static propTypes = {
    errors: PropTypes.object.isRequired,
    values: PropTypes.object.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    destinations: PropTypes.array.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    isValid: PropTypes.bool.isRequired,
    configurations: PropTypes.shape({
      maxChildrenCount: PropTypes.number,
      maxAdultsCount: PropTypes.number,
      maxRoomsCount: PropTypes.number,
      maxChildrenAge: PropTypes.number,
      hotelsPerPage: PropTypes.number,
    }).isRequired,
    handleDestinationSelect: PropTypes.func,
    defaultSort: PropTypes.string.isRequired,
    router: PropTypes.shape({
      push: PropTypes.func,
    }).isRequired,
    disabled: PropTypes.bool,
    editMode: PropTypes.bool,
    toggleForm: PropTypes.func,
    onlyPartyConfig: PropTypes.bool,
    themeColor: PropTypes.oneOf(['blueSearchWidget', 'greySearchWidget']),
    theme: PropTypes.object.isRequired,

    translations: PropTypes.shape({
      destinationLabel: PropTypes.string.isRequired,
      destinationPlaceholder: PropTypes.string.isRequired,
      checkInLabel: PropTypes.string.isRequired,
      checkInPlaceholder: PropTypes.string.isRequired,
      checkOutLabel: PropTypes.string.isRequired,
      checkOutPlaceholder: PropTypes.string.isRequired,
      roomNoLabel: PropTypes.string.isRequired,
      adultsLabel: PropTypes.string.isRequired,
      childrenLabel: PropTypes.string.isRequired,
      ageAtCheckInLabel: PropTypes.string.isRequired,
      removeRoomButton: PropTypes.string.isRequired,
      roomNumber: PropTypes.string.isRequired,
      searchButtonText: PropTypes.string.isRequired,
      updateSearchButtonText: PropTypes.string.isRequired,
      cancelSearchButtonText: PropTypes.string.isRequired,
      viewModifyResevation: PropTypes.string.isRequired,
    }).isRequired,
  };

  static defaultProps = {
    editMode: false,
    toggleForm: Function.prototype,
    onlyPartyConfig: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      datePickerMode: null,
      formStartGAEventCalled: false,
    };
    this.setDatePickerMode = this.setDatePickerMode.bind(this);

    this.toggleCheckInPicker = this.setDatePickerMode.bind(this, MODE_START);
    this.toggleCheckOutPicker = this.setDatePickerMode.bind(this, MODE_END);
    this.hideDatePicker = this.setDatePickerMode.bind(this, null);
    this.handleCheckInChange = this.handleCheckInChange.bind(this);

    this.checkInRef = React.createRef();
    this.checkOutRef = React.createRef();
  }
  static contextType = AnalyticsContext;

  componentDidUpdate(prevProps) {
    if (
      !this.state.formStartGAEventCalled &&
      this.props.values &&
      prevProps.values !== this.props.values
    ) {
      tracking.trackEvent({
        event: GA4_EVENTS.FORM_START,
        form_name: 'Hotel Search Form',
      });
      this.setState({
        formStartGAEventCalled: true,
      });
    }
  }

  setDatePickerMode(mode) {
    this.setState({
      datePickerMode: mode === this.state.datePickerMode ? null : mode,
    });
  }

  handleCheckInChange(checkIn) {
    const checkOut = this.props.values[CHECK_OUT_FIELD];

    if (checkIn && checkOut) {
      if (
        checkIn.isAfter(checkOut) ||
        checkOut.diff(checkIn, 'days') > 30 ||
        checkIn.isSame(checkOut)
      ) {
        this.props.setFieldValue(CHECK_OUT_FIELD, '');
      }
    }
    this.setState({
      datePickerMode: MODE_END,
    });
  }

  handleDestinationChange() {
    const { values, destinations } = this.props;

    let destination = destinations.find(
      destination => destination.id === values.locationCode
    );

    let destinationCode = destination && destination.code;
    this.props.handleDestinationSelect &&
      this.props.handleDestinationSelect(destinationCode);
  }

  genChildAges() {
    const {
      configurations: { maxChildrenAge },
    } = this.props;

    let ages = range(maxChildrenAge).map(age => ({
      label: `${age + 1}`,
      value: age + 1,
    }));

    ages[0] = { label: '<1', value: 0 };
    return ages;
  }

  renderFirstInputRow(mq) {
    const {
      destinations,
      values,
      translations: {
        destinationLabel,
        destinationPlaceholder,
        checkInLabel,
        checkInPlaceholder,
        checkOutLabel,
        checkOutPlaceholder,
      },
      themeColor,
      theme,
    } = this.props;

    const mainList = destinations.filter(d => d.isMain);
    const secondaryList = destinations.filter(d => !d.isMain);

    let destinationsList = [...formatList(mainList)];

    secondaryList.length &&
      destinationsList.push({
        label: 'surroundings',
        options: [...formatList(secondaryList)],
      });

    return (
      <Row gutter={getRowGutter(mq)}>
        <Col lg={12}>
          <Label>
            <FieldName themeColor={themeColor} children={destinationLabel} />
            <FieldHandler withErrorMessage={false} name={DESTINATION_FIELD}>
              <CustomSelectNoSSR
                withDropDownIndicator={false}
                optionColor={theme.colors.hob.destinationPicker.options}
                placeholder={destinationPlaceholder}
                formatGroupLabel={
                  secondaryList.length ? () => <Divider /> : undefined
                }
                options={destinationsList}
                noOptionsMessage={() => 'No matching destinations found'}
                onChange={this.handleDestinationChange()}
              />
            </FieldHandler>
          </Label>
        </Col>

        <Col span={12} lg={6}>
          <div ref={this.checkInRef}>
            <Label>
              <FieldName themeColor={themeColor} children={checkInLabel} />
              <InputMask
                name={CHECK_IN_FIELD}
                key={CHECK_IN_FIELD}
                onClick={this.toggleCheckInPicker}
                automationHook={OPEN_CHECK_IN}
                disabled={!values[DESTINATION_FIELD]}
                active={this.state.datePickerMode === MODE_START}
                value={
                  values[CHECK_IN_FIELD] &&
                  moment(values[CHECK_IN_FIELD]).format('MMM DD, YYYY')
                }
                placeholder={checkInPlaceholder}
              />
            </Label>
          </div>
        </Col>

        {this.state.datePickerMode === MODE_START ? (
          <FloatedCol span={24}>
            <OutsideListener
              refs={[this.checkInRef, this.checkOutRef]}
              handleClickOutside={this.hideDatePicker}
            >
              <FieldHandler
                name={CHECK_IN_FIELD}
                onChange={this.handleCheckInChange}
              >
                <DatePicker
                  key={MODE_START}
                  {...this.props}
                  mode={MODE_START}
                  start={values[CHECK_IN_FIELD] || undefined}
                  end={values[CHECK_OUT_FIELD] || undefined}
                  singleCalendar={mq.lte('md') ? true : undefined}
                />
              </FieldHandler>
            </OutsideListener>
          </FloatedCol>
        ) : null}

        <Col span={12} lg={6}>
          <div ref={this.checkOutRef}>
            <Label>
              <FieldName themeColor={themeColor} children={checkOutLabel} />
              <InputMask
                name={CHECK_IN_FIELD}
                key={CHECK_IN_FIELD}
                onClick={this.toggleCheckOutPicker}
                automationHook={OPEN_CHECKOUT}
                active={this.state.datePickerMode === MODE_END}
                disabled={!values[CHECK_IN_FIELD]}
                value={
                  values[CHECK_OUT_FIELD] &&
                  moment(values[CHECK_OUT_FIELD]).format('MMM DD, YYYY')
                }
                placeholder={checkOutPlaceholder}
              />
            </Label>
          </div>
        </Col>

        {this.state.datePickerMode === MODE_END ? (
          <Col span={24}>
            <OutsideListener
              refs={[this.checkInRef, this.checkOutRef]}
              handleClickOutside={this.hideDatePicker}
            >
              <FieldHandler
                name={CHECK_OUT_FIELD}
                onChange={this.hideDatePicker}
              >
                <DatePicker
                  key={MODE_END}
                  {...this.props}
                  mode={MODE_END}
                  start={values[CHECK_IN_FIELD] || moment()}
                  end={values[CHECK_OUT_FIELD] || undefined}
                  singleCalendar={mq.lte('md') ? true : undefined}
                />
              </FieldHandler>
            </OutsideListener>
          </Col>
        ) : null}
      </Row>
    );
  }

  renderPartyPickerContainer(mq) {
    const {
      values,
      setFieldValue,
      configurations: { maxChildrenCount, maxAdultsCount, maxRoomsCount },
      translations: {
        roomNoLabel,
        adultsLabel,
        childrenLabel,
        ageAtCheckInLabel,
        removeRoomButton,
        roomNumber,
      },
      themeColor,
      theme,
    } = this.props;

    const roomParty = values[ROOM_PARTY];
    const roomPartyLength = values[ROOM_PARTY].length;
    const twoOrMoreRooms = values[ROOM_PARTY].length > 1;

    const childrenAges = i => values[ROOM_PARTY][i][CHILDREN_AGES];

    const oddChildrenFields = i =>
      values[ROOM_PARTY][i][CHILDREN_FIELD] % 2 !== 0;

    const isEven = i => i % 2 === 0;
    const isEveryThird = i => i % 3 === 0;

    const border = theme.colors.hob[themeColor].fieldBorderValue
      ? `solid 1px ${theme.colors.hob[themeColor].fieldBorderValue}`
      : undefined;

    return (
      <FieldArray name={ROOM_PARTY}>
        {roomArrayHelpers => (
          <Row gutter={getRowGutter(mq)} type={'flex'} align={'bottom'}>
            <Col span={24} lg={4}>
              <FieldName themeColor={themeColor} children={roomNoLabel} />
              <FieldHandler
                onChange={(value, prevValue) => {
                  if (prevValue < value) {
                    roomArrayHelpers.push({
                      [ADULTS_FIELD]: 1,
                      [CHILDREN_FIELD]: 0,
                      [CHILDREN_AGES]: [],
                    });
                  } else {
                    roomArrayHelpers.remove(roomPartyLength - 1);
                  }
                }}
                name={ROOMS_FIELD}
              >
                <TempQuantitySelector
                  border={border}
                  min={1}
                  max={maxRoomsCount}
                  decrementButtonAriaLabel="decrement"
                  incrementButtonAriaLabel="increment"
                  valueSelectedAriaLabel="selected"
                  quantitySelectorAriaLabel="quantity"
                  automationHookDecrement={DECREMENT_ROOMS}
                  automationHookIncrement={INCREMENT_ROOMS}
                  automationHookValue={ROOMS_QUANTITY}
                />
              </FieldHandler>
            </Col>

            {roomParty.map((value, i) => (
              <React.Fragment key={i}>
                {twoOrMoreRooms ? (
                  <Col
                    span={6}
                    lg={{
                      span: 3,
                      ...(i > 0 && { offset: 4 }),
                    }}
                  >
                    <Box>
                      <Text
                        size={14}
                        weight={500}
                        color={theme.colors.hob[themeColor].roomNum}
                        children={
                          <Markdown
                            source={roomNumber}
                            data={{ number: i + 1 }}
                          />
                        }
                      />
                    </Box>

                    {i > 0 ? (
                      <PlainButton
                        automationHook={withValue(REMOVE_ROOM, i)}
                        type={
                          themeColor === 'greySearchWidget'
                            ? 'secondary'
                            : undefined
                        }
                        underlined
                        onClick={() => {
                          roomArrayHelpers.remove(i);
                          setFieldValue(ROOMS_FIELD, values[ROOMS_FIELD] - 1);
                        }}
                      >
                        <Text size={10}>{removeRoomButton}</Text>
                      </PlainButton>
                    ) : null}
                  </Col>
                ) : null}
                <Col span={twoOrMoreRooms ? 9 : 12} lg={4}>
                  <FieldName themeColor={themeColor} children={adultsLabel} />
                  <FieldHandler name={`${ROOM_PARTY}.${i}.${ADULTS_FIELD}`}>
                    <TempQuantitySelector
                      border={border}
                      min={1}
                      max={maxAdultsCount}
                      decrementButtonAriaLabel="decrement"
                      incrementButtonAriaLabel="increment"
                      valueSelectedAriaLabel="selected"
                      quantitySelectorAriaLabel="quantity"
                      automationHookDecrement={DECREMENT_ADULTS}
                      automationHookIncrement={INCREMENT_ADULTS}
                      automationHookValue={ADULTS_QUANTITY}
                    />
                  </FieldHandler>
                </Col>

                <FieldArray name={`${ROOM_PARTY}.${i}.${CHILDREN_AGES}`}>
                  {childrenDobArrayHelpers => (
                    <React.Fragment>
                      <Col span={twoOrMoreRooms ? 9 : 12} lg={4}>
                        <FieldName
                          themeColor={themeColor}
                          children={childrenLabel}
                        />

                        <FieldHandler
                          name={`${ROOM_PARTY}.${i}.${CHILDREN_FIELD}`}
                          onChange={(value, prevValue) => {
                            if (prevValue < value) {
                              childrenDobArrayHelpers.push(-1);
                            } else {
                              childrenDobArrayHelpers.remove(
                                childrenAges(i).length - 1
                              );
                            }
                          }}
                        >
                          <TempQuantitySelector
                            border={border}
                            min={0}
                            max={maxChildrenCount}
                            decrementButtonAriaLabel="decrement"
                            incrementButtonAriaLabel="increment"
                            valueSelectedAriaLabel="selected"
                            quantitySelectorAriaLabel="quantity"
                            automationHookDecrement={DECREMENT_CHILDREN}
                            automationHookIncrement={INCREMENT_CHILDREN}
                            automationHookValue={CHILDREN_QUANTITY}
                          />
                        </FieldHandler>
                      </Col>
                      {childrenAges(i).length === 0 ? (
                        <Col span={0} lg={8} />
                      ) : null}
                      {childrenAges(i).map((v, index) => (
                        <Col
                          key={index}
                          span={twoOrMoreRooms ? 9 : 12}
                          {...(isEven(index) &&
                            twoOrMoreRooms && { offset: 6 })}
                          lg={{
                            span: twoOrMoreRooms ? 3 : 4,
                            offset: 0,
                            ...(index > 2 &&
                              isEveryThird(index) && {
                                offset: twoOrMoreRooms ? 15 : 12,
                              }),
                          }}
                        >
                          <Box d={'flex'} direction={'column'}>
                            <FieldName
                              themeColor={themeColor}
                              children={
                                <Markdown
                                  source={ageAtCheckInLabel}
                                  data={{ number: index + 1 }}
                                />
                              }
                            />
                            <FieldHandler
                              withErrorMessage={false}
                              name={`${ROOM_PARTY}.${i}.${CHILDREN_AGES}.${index}`}
                            >
                              <CustomSelectNoSSR
                                options={this.genChildAges()}
                                automationHook={withValue(
                                  SELECT_CHILD_AGE,
                                  `${index}_${values[ROOM_PARTY][i][CHILDREN_AGES][index]}`
                                )}
                                placeholder="Age"
                                thickStyle={themeColor === 'greySearchWidget'}
                                name={`${ROOM_PARTY}.${i}.${CHILDREN_AGES}.${index}`}
                              />
                            </FieldHandler>
                          </Box>
                        </Col>
                      ))}
                      {oddChildrenFields(i) && twoOrMoreRooms ? (
                        <Col span={9} lg={0} />
                      ) : null}
                    </React.Fragment>
                  )}
                </FieldArray>
              </React.Fragment>
            ))}
          </Row>
        )}
      </FieldArray>
    );
  }

  renderFinalInputRow(mq) {
    const {
      isValid,
      editMode,
      toggleForm,
      router,
      translations: {
        searchButtonText,
        viewModifyResevation,
        updateSearchButtonText,
        cancelSearchButtonText,
      },
      themeColor,
    } = this.props;

    if (editMode) {
      return (
        <Box
          d="flex"
          {...(mq.lte('md') && { direction: 'column' })}
          justifyContent="flex-end"
          alignItems="center"
        >
          {!mq.lte('md') ? (
            <Box mr={6}>
              <PlainButton
                automationHook={CLOSE_SEARCH_BUTTON}
                onClick={toggleForm}
                upperCase
                type={
                  themeColor === 'greySearchWidget' ? 'secondary' : undefined
                }
              >
                <Text size={14}>{cancelSearchButtonText}</Text>
              </PlainButton>
            </Box>
          ) : null}
          <TrackClick
            category={GA4_EVENTS.SITE_CLICK_TRACKING}
            action="Update Search for hotels"
            label="Update Search Hotels"
          >
            <SearchFormButton
              automationHook={SUBMIT_HOTEL_SEARCH}
              disabled={!isValid}
              editMode={editMode}
              htmlType="submit"
              fullWidth={mq.lte('md')}
            >
              {updateSearchButtonText}
            </SearchFormButton>
          </TrackClick>
          {mq.lte('md') ? <VerticalSpacing size={6} /> : null}
        </Box>
      );
    }

    return (
      <Box
        d="flex"
        {...(mq.lte('md') && { direction: 'column' })}
        justifyContent="space-between"
        alignItems="center"
      >
        <TrackClick
          category={GA4_EVENTS.SITE_CLICK_TRACKING}
          action="HLO Booking Widget"
          label="Search Hotels"
        >
          <SearchFormButton
            automationHook={SUBMIT_HOTEL_SEARCH}
            disabled={!isValid}
            htmlType="submit"
          >
            {searchButtonText}
          </SearchFormButton>
        </TrackClick>

        <TrackClick
          category={GA4_EVENTS.SITE_CLICK_TRACKING}
          action={'HO Widget View/Modify'}
          label={'View or modify your reservation'}
        >
          <PlainButton
            onClick={() => {
              router.push('/hotel-booking/find-reservation');
            }}
            underlined
          >
            <Text size={14}>{viewModifyResevation}</Text>
          </PlainButton>
        </TrackClick>

        {mq.lte('md') ? <VerticalSpacing size={6} /> : null}
      </Box>
    );
  }

  render() {
    const { handleSubmit, onlyPartyConfig } = this.props;

    return (
      <Media>
        {mq => (
          <form onSubmit={handleSubmit}>
            {!onlyPartyConfig ? (
              <Row gutter={getRowGutter(mq)}>
                <Col sm={24}>{this.renderFirstInputRow(mq)}</Col>
              </Row>
            ) : null}

            <Row gutter={getRowGutter(mq)}>
              <Col xs={24}>{this.renderPartyPickerContainer(mq)}</Col>
            </Row>
            <Row gutter={getRowGutter(mq)}>
              <Col xs={24}>
                <VerticalSpacing size={6} />
                {this.renderFinalInputRow(mq)}
              </Col>
            </Row>
          </form>
        )}
      </Media>
    );
  }
}

const SearchFormWithFormik = withFormik({
  mapPropsToValues: ({ defaultValues }) => ({
    ...defaultValues,
  }),

  validationSchema: searchFormSchema,

  handleSubmit: (values, { props }) => {
    props.toggleForm && props.toggleForm();

    const {
      router: { route, query, push },
      submitToPath,
      configurations: { hotelsPerPage },
      defaultSort,
      editMode,
    } = props;

    /*
      Form expose room data something like this:
        rooms: [{adults: 2, childrenAges: [5, 9]}, {adults: 1, childrenAges: []}]
      We need to "encode" that to be processed by router, and the output will be:
        rooms: ['2,5,9', '1']
    */
    const rooms = values[ROOM_PARTY].map(room =>
      room[CHILDREN_AGES].length
        ? `${room[ADULTS_FIELD].toString()}, ${room[CHILDREN_AGES].join()}`
        : room[ADULTS_FIELD].toString()
    );

    const destination = props.destinations.find(
      d => d.id === values[DESTINATION_FIELD]
    );

    const routerOpts = {
      pathname: `${submitToPath || route}`,
      query: {
        ...query,
        [DESTINATION_FIELD]: destination.code,
        [CHECK_IN_FIELD]: values[CHECK_IN_FIELD].format('YYYY-MM-DD'),
        [CHECK_OUT_FIELD]: values[CHECK_OUT_FIELD].format('YYYY-MM-DD'),
        rooms,
        sort: defaultSort,
        limit: hotelsPerPage,
        offset: 0,
      },
    };

    push(routerOpts);

    if (editMode) {
      tracking.trackModifySearch(destination.code);
    } else {
      tracking.trackFormSubmit('Hotel Search Form', 'Search Hotels', 'By Text');
    }
  },

  displayName: 'SearchForm',
})(SearchForm);

export default withTheme(withRouter(SearchFormWithFormik));
